import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Card, Button, Dropdown, Spinner, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DatePeriodNavigation, { PeriodChangeEvent } from '../components/common/DatePeriodNavigation';
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { getInvoices, getInvoicesExport, useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Invoice, InvoicesSummary } from '../api/generated/model';
import { RootState } from '../store';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import InvoiceFilterBar, { InvoiceFiltersState } from '../components/InvoiceFilterBar';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';

const FETCH_LIMIT = 50;

const formatCurrency = (amount?: string): string => {
  if (!amount) return '';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime()) || d.getFullYear() < 1900) return '';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const statusBadge = (status?: string) => {
  const variants: Record<string, string> = {
    NotDue: 'primary',
    OverDue: 'danger',
    PaidInFull: 'success',
    OverPaid: 'warning',
    Voided: 'dark',
  };
  const labels: Record<string, string> = {
    NotDue: 'Not Due',
    OverDue: 'Overdue',
    PaidInFull: 'Paid',
    OverPaid: 'Overpaid',
    Voided: 'Voided',
  };
  return (
    <Badge bg={variants[status || ''] || 'secondary'}>
      {labels[status || ''] || status || 'Unknown'}
    </Badge>
  );
};

type SortField = 'invoice_number' | 'invoice_date' | 'booking_date' | 'gross_amount' | 'net_amount' | 'balance_due' | 'due_date' | 'status' | 'created_at' | 'sales_person' | 'sales_lead' | 'program_count' | 'other_count' | 'payment_count';

type DateFieldOption = 'invoice_date' | 'booking_date' | 'due_date';
const dateFieldLabels: Record<DateFieldOption, string> = {
  invoice_date: 'Invoice Date',
  booking_date: 'Booking Date',
  due_date: 'Due Date',
};

const filterParamKeys = ['invoice_number', 'customer', 'sales_person', 'sales_lead', 'purchase_order', 'status', 'voided', 'has_attachments', 'has_valid_po', 'has_vp_waiver', 'has_valid_invoice', 'has_parse_errors'] as const;
const validPeriods = ['month', 'quarter', 'year', 'custom', 'all'] as const;
type Period = typeof validPeriods[number];

/** Return YYYY-MM-DD if valid, or null */
function parseDate(s: string | null): string | null {
  if (!s) return null;
  const parts = s.split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]), m = Number(parts[1]), day = Number(parts[2]);
  if (isNaN(y) || isNaN(m) || isNaN(day)) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthRange(d: Date): { start_date: string; end_date: string } {
  const y = d.getFullYear(), m = d.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  return { start_date: start.toLocaleDateString('en-CA'), end_date: end.toLocaleDateString('en-CA') };
}

function quarterRange(d: Date): { start_date: string; end_date: string } {
  const y = d.getFullYear(), q = Math.floor(d.getMonth() / 3);
  const start = new Date(y, q * 3, 1);
  const end = new Date(y, q * 3 + 3, 0);
  return { start_date: start.toLocaleDateString('en-CA'), end_date: end.toLocaleDateString('en-CA') };
}

function yearRange(d: Date): { start_date: string; end_date: string } {
  const y = d.getFullYear();
  return { start_date: `${y}-01-01`, end_date: `${y}-12-31` };
}

function periodRange(period: Period): { start_date: string; end_date: string } {
  const now = new Date();
  if (period === 'quarter') return quarterRange(now);
  if (period === 'year') return yearRange(now);
  return monthRange(now); // month or custom default
}

/** Parse and validate URL params into initial period, start_date, end_date */
function parseInitialDateParams(params: URLSearchParams): { period: Period; start_date: string; end_date: string } {
  const rawPeriod = params.get('period');
  const period: Period = rawPeriod && (validPeriods as readonly string[]).includes(rawPeriod)
    ? rawPeriod as Period
    : 'month';

  // 'all' mode: emit empty dates so the API call drops the start/end params.
  if (period === 'all') {
    return { period, start_date: '', end_date: '' };
  }

  let startDate = parseDate(params.get('start_date'));
  let endDate = parseDate(params.get('end_date'));

  if (period !== 'custom') {
    const defaults = periodRange(period);
    if (!startDate) startDate = defaults.start_date;
    if (!endDate) endDate = defaults.end_date;
  } else {
    const defaults = monthRange(new Date());
    if (!startDate) startDate = defaults.start_date;
    if (!endDate) endDate = defaults.end_date;
  }

  // Swap if end < start
  if (startDate > endDate) {
    [startDate, endDate] = [endDate, startDate];
  }

  return { period, start_date: startDate, end_date: endDate };
}

const InvoicesPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', {
    query: { enabled: !!user?.id },
  });
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse and validate URL date params once on mount
  const [initialDateParams] = useState(() => parseInitialDateParams(searchParams));

  // Initialize state from URL params
  const [sortField, setSortField] = useState<SortField>(
    (searchParams.get('sort_field') as SortField) || 'invoice_date'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (searchParams.get('sort_dir') as 'asc' | 'desc') || 'desc'
  );
  const [dateField, setDateField] = useState<DateFieldOption>(
    (searchParams.get('date_field') as DateFieldOption) || 'invoice_date'
  );
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState<Invoice | null>(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [period, setPeriod] = useState<Period>(initialDateParams.period);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Filters state — initialize from URL params (YYYY-MM-DD)
  const [filters, setFilters] = useState<InvoiceFiltersState>(() => {
    const initial: InvoiceFiltersState = {
      dateRange: {
        start_date: initialDateParams.start_date,
        end_date: initialDateParams.end_date,
      },
    };
    for (const key of filterParamKeys) {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    }
    return initial;
  });

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((invoice: Invoice) => invoice.id || '', []);

  // Build query params - for cursor-based pagination, only send cursor + limit
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            date_field: dateField,
            start_date: filters.dateRange.start_date || undefined,
            end_date: filters.dateRange.end_date || undefined,
            sort_field: sortField,
            sort_direction: sortDirection,
            invoice_number: filters.invoice_number,
            customer: filters.customer,
            sales_person: filters.sales_person,
            sales_lead: filters.sales_lead,
            purchase_order: filters.purchase_order,
            status: filters.status,
            voided: filters.voided,
            has_attachments: filters.has_attachments,
            has_valid_po: filters.has_valid_po,
            has_vp_waiver: filters.has_vp_waiver,
            has_valid_invoice: filters.has_valid_invoice,
            has_parse_errors: filters.has_parse_errors,
            limit: FETCH_LIMIT,
          },
    [dateField, filters, sortField, sortDirection]
  );

  const {
    allItems: allInvoices,
    totalCount,
    apiSummary,
    isLoading,
    hasMore,
    handleLoadMore,
    setAllItems: setAllInvoices,
  } = usePaginatedQuery<Invoice, Awaited<ReturnType<typeof getInvoices>>, InvoicesSummary>({
    queryKey: 'invoices',
    buildQueryParams,
    queryFn: (params) => getInvoices(params as any),
    getRowKey,
    enabled: period === 'all' || (!!filters.dateRange.start_date && !!filters.dateRange.end_date),
  });

  // Handle date period changes
  const handlePeriodChange = (event: PeriodChangeEvent) => {
    setPeriod(event.navigationMode as Period);
    setFilters(prev => {
      if (prev.dateRange.start_date === event.dateRange.start_date &&
          prev.dateRange.end_date === event.dateRange.end_date) {
        return prev; // No change — don't trigger re-fetch
      }
      return { ...prev, dateRange: event.dateRange };
    });
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: InvoiceFiltersState) => {
    setFilters(newFilters);
  };

  // Toggle the has_parse_errors filter (clicking the Errors summary card)
  const handleToggleErrorsFilter = () => {
    setFilters(prev => ({
      ...prev,
      has_parse_errors: prev.has_parse_errors === 'true' ? undefined : 'true',
    }));
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters(prev => ({
      dateRange: prev.dateRange,
    }));
  };

  // Sync filter/sort state to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (filters.dateRange.start_date) params.set('start_date', filters.dateRange.start_date);
    if (filters.dateRange.end_date) params.set('end_date', filters.dateRange.end_date);
    for (const key of filterParamKeys) {
      if (filters[key]) params.set(key, filters[key]!);
    }
    if (dateField !== 'invoice_date') params.set('date_field', dateField);
    if (sortField !== 'invoice_date') params.set('sort_field', sortField);
    if (sortDirection !== 'desc') params.set('sort_dir', sortDirection);
    setSearchParams(params, { replace: true });
  }, [filters, period, dateField, sortField, sortDirection, setSearchParams]);

  // Count active filters (excluding dateRange)
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    return value && key !== 'dateRange';
  }).length;

  const totalPaid = (() => {
    if (!apiSummary?.total_net_amount || !apiSummary?.total_balance_due) return '';
    const net = parseFloat(apiSummary.total_net_amount);
    const balance = parseFloat(apiSummary.total_balance_due);
    if (isNaN(net) || isNaN(balance)) return '';
    return (net - balance).toFixed(2);
  })();

  const stickyHeaderContent = (
    <div className="d-flex gap-3 align-items-stretch flex-wrap">
      <Card className="">
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-receipt fs-1 text-primary"></i>
          </div>
          <h3 className="mb-1">{totalCount ?? allInvoices.length}</h3>
          <p className="text-muted mb-0 small">Invoices</p>
        </Card.Body>
      </Card>
      <Card className="">
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-controller fs-1 text-info"></i>
          </div>
          <h3 className="mb-1">{apiSummary?.total_programs ?? 0}</h3>
          <p className="text-muted mb-0 small">Programs</p>
        </Card.Body>
      </Card>
      <Card
        className={`invoices-summary-card ${filters.has_parse_errors === 'true' ? 'invoices-summary-card-active' : ''}`}
        onClick={handleToggleErrorsFilter}
        style={{ cursor: 'pointer' }}
      >
        <Card.Body className="text-center p-2">
          <div className="mb-2">
            <i className="bi bi-exclamation-triangle fs-1 text-danger"></i>
          </div>
          <h3 className="mb-1">{apiSummary?.total_errors ?? 0}</h3>
          <p className="text-muted mb-0 small">With Errors</p>
        </Card.Body>
      </Card>
      <Card className="">
        <Card.Body className="p-2 d-flex align-items-center">
          <table className="table table-sm table-borderless mb-0" style={{ whiteSpace: 'nowrap' }}>
            <tbody>
              <tr>
                <td className="text-muted">Gross Amount</td>
                <td className="text-end fw-semibold" style={{ paddingRight: '1rem' }}>{formatCurrency(apiSummary?.total_gross_amount)}</td>
                <td className="text-muted" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>Total Paid</td>
                <td className="text-end fw-semibold text-success" style={{ paddingRight: '1rem' }}>{formatCurrency(totalPaid)}</td>
                <td className="text-muted" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>Overdue</td>
                <td className="text-end fw-semibold text-danger">{formatCurrency(apiSummary?.total_overdue)}</td>
              </tr>
              <tr>
                <td className="text-muted">Discounts</td>
                <td className="text-end text-danger" style={{ paddingRight: '1rem' }}>{formatCurrency(apiSummary?.total_discount)}</td>
                <td className="text-muted" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>Balance Due</td>
                <td className="text-end fw-semibold" style={{ paddingRight: '1rem' }}>{formatCurrency(apiSummary?.total_balance_due)}</td>
                <td className="text-muted" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>Not Yet Due</td>
                <td className="text-end fw-semibold text-warning">{formatCurrency(apiSummary?.total_not_due)}</td>
              </tr>
              <tr>
                <td className="text-muted">Net Amount</td>
                <td className="text-end fw-semibold" style={{ paddingRight: '1rem' }}>{formatCurrency(apiSummary?.total_net_amount)}</td>
                <td style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}></td>
                <td style={{ paddingRight: '1rem' }}></td>
                <td className="text-muted" style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '1rem' }}>Overpaid</td>
                <td className="text-end fw-semibold text-primary">{formatCurrency(apiSummary?.total_overpaid)}</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>
    </div>
  );

  const columns: ColumnConfig<Invoice>[] = useMemo(() => [
    {
      key: 'invoice_number',
      label: 'Invoice #',
      sortable: true,
      width: 120,
      renderCell: (invoice) => invoice.invoice_number || '',
    },
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      width: 200,
      renderCell: (invoice) => invoice.customer?.name || '',
    },
    {
      key: 'invoice_date',
      label: 'Invoice Date',
      sortable: true,
      width: 159,
      renderCell: (invoice) => formatDate(invoice.invoice_date),
    },
    {
      key: 'booking_date',
      label: 'Booking Date',
      sortable: true,
      width: 159,
      renderCell: (invoice) => formatDate(invoice.booking_date),
    },
    {
      key: 'gross_amount',
      label: 'Gross Amount',
      sortable: true,
      width: 166,

      renderCell: (invoice) => (
        <div style={{ textAlign: 'right', width: '100%' }}>{formatCurrency(invoice.invoice_amount)}</div>
      ),
    },
    {
      key: 'net_amount',
      label: 'Net Amount',
      sortable: true,
      width: 148,
      renderCell: (invoice) => (
        <div style={{ textAlign: 'right', width: '100%' }}>{formatCurrency(invoice.net_amount)}</div>
      ),
    },
    {
      key: 'balance_due',
      label: 'Balance Due',
      sortable: true,
      width: 152,
      renderCell: (invoice) => (
        <div style={{ textAlign: 'right', width: '100%' }}>{formatCurrency(invoice.balance_due)}</div>
      ),
    },
    {
      key: 'due_date',
      label: 'Due Date',
      sortable: true,
      width: 130,
      renderCell: (invoice) => formatDate(invoice.due_date),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 123,
      renderCell: (invoice) => statusBadge(invoice.status),
    },
    {
      key: 'sales_person',
      label: 'Sales Person',
      sortable: true,
      width: 156,
      renderCell: (invoice) => invoice.sales_person || '',
    },
    {
      key: 'sales_lead',
      label: 'Sales Lead',
      sortable: true,
      width: 150,
      renderCell: (invoice) => invoice.sales_lead || '',
    },
    {
      key: 'purchase_order',
      label: 'PO #',
      sortable: true,
      width: 150,
      renderCell: (invoice) => invoice.purchase_order || '',
    },
    {
      key: 'program_count',
      label: 'Programs',
      sortable: true,
      width: 110,
      renderCell: (invoice) => {
        const count = invoice.program_count || 0;
        return count > 0 ? <Badge bg="info">{count}</Badge> : <span className="text-muted">0</span>;
      },
    },
    {
      key: 'other_count',
      label: 'Other Lines',
      sortable: true,
      width: 145,
      renderCell: (invoice) => {
        const count = invoice.other_line_count || 0;
        return count > 0 ? <Badge bg="secondary">{count}</Badge> : <span className="text-muted">0</span>;
      },
    },
    {
      key: 'payment_count',
      label: 'Payments',
      sortable: true,
      width: 110,
      renderCell: (invoice) => {
        const count = invoice.payments?.length || 0;
        return count > 0 ? <Badge bg="success">{count}</Badge> : <span className="text-muted">0</span>;
      },
    },
    {
      key: 'attachment_count',
      label: 'Attachments',
      sortable: true,
      width: 110,
      renderCell: (invoice) => {
        const count = invoice.attachment_count || 0;
        return count > 0 ? <Badge bg="primary">{count}</Badge> : <span className="text-muted">0</span>;
      },
    },
    {
      key: 'error_count',
      label: 'Errors',
      sortable: true,
      width: 90,
      renderCell: (invoice) => {
        const count = invoice.error_count || 0;
        return count > 0 ? <Badge bg="danger">{count}</Badge> : <span className="text-muted">0</span>;
      },
      tooltip: () => 'Line items with parse errors that need fixing in QuickBooks',
    },
    {
      key: 'has_valid_po',
      label: 'PO',
      sortable: true,
      width: 55,
      renderCell: (invoice) => invoice.has_valid_po ? <i className="bi bi-check-circle-fill text-success"></i> : <i className="bi bi-circle text-muted"></i>,
      tooltip: () => 'Valid PO or signed contract',
    },
    {
      key: 'has_vp_waiver',
      label: 'VP',
      sortable: true,
      width: 50,
      renderCell: (invoice) => invoice.has_vp_waiver ? <i className="bi bi-check-circle-fill text-success"></i> : <i className="bi bi-circle text-muted"></i>,
      tooltip: () => 'VP of Sales waiver',
    },
    {
      key: 'has_valid_invoice',
      label: 'Valid',
      sortable: true,
      width: 55,
      renderCell: (invoice) => invoice.has_valid_invoice ? <i className="bi bi-check-circle-fill text-success"></i> : <i className="bi bi-circle text-muted"></i>,
      tooltip: () => 'Invoice reviewed and validated',
    },
  ], []);

  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field as SortField);
    setSortDirection(direction);
  }, []);

  // Visible columns in display order, mirroring useColumnManagement's merge of
  // defaults with saved preferences. Every visibility/order change persists
  // immediately (and updates the useGetUsersId query cache), so the saved
  // state reflects what the table currently shows.
  const computeExportFields = (): string => {
    const saved = userData?.preferences?.table_column_orders?.['invoices'];
    if (!saved || saved.length === 0) {
      return columns.map(c => c.key).join(',');
    }
    const ordered = saved
      .filter(cfg => cfg.visible && columns.some(c => c.key === cfg.key))
      .map(cfg => cfg.key);
    const savedKeys = new Set(saved.map(cfg => cfg.key));
    const extras = columns.filter(c => !savedKeys.has(c.key)).map(c => c.key);
    return [...ordered, ...extras].join(',');
  };

  const handleExportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await getInvoicesExport({
        date_field: dateField,
        start_date: filters.dateRange.start_date || undefined,
        end_date: filters.dateRange.end_date || undefined,
        sort_field: sortField,
        sort_direction: sortDirection,
        invoice_number: filters.invoice_number,
        customer: filters.customer,
        sales_person: filters.sales_person,
        sales_lead: filters.sales_lead,
        purchase_order: filters.purchase_order,
        status: filters.status,
        voided: filters.voided,
        has_attachments: filters.has_attachments,
        has_valid_po: filters.has_valid_po,
        has_vp_waiver: filters.has_vp_waiver,
        has_valid_invoice: filters.has_valid_invoice,
        has_parse_errors: filters.has_parse_errors,
        fields: computeExportFields(),
      } as any);
      const blob = new Blob([response as any], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toLocaleDateString('en-CA')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      const apiError = error?.response?.data?.message || error?.message || 'Failed to export CSV';
      setExportError(apiError);
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardLayout title="Invoices" stickyHeader={stickyHeaderContent} scrollable>
      <style>{`
        .summary-cards-row {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 1rem !important;
          margin-bottom: 1rem !important;
        }
        .summary-card-col {
          flex: 0 0 auto !important;
        }
        .invoices-summary-card {
          transition: all 0.2s ease;
          border: 2px solid transparent !important;
        }
        .invoices-summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }
        .invoices-summary-card-active {
          border: 2px solid #dc3545 !important;
          background-color: rgba(220, 53, 69, 0.05);
        }
      `}</style>

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">
                  <i className="bi bi-receipt me-1"></i>
                  Invoices by
                </span>
                <Dropdown className="d-inline-block">
                  <Dropdown.Toggle variant="outline-primary" size="sm">
                    {dateFieldLabels[dateField]}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="bg-header-control" data-bs-theme="light">
                    {(Object.keys(dateFieldLabels) as DateFieldOption[]).map(key => (
                      <Dropdown.Item key={key} active={dateField === key} onClick={() => setDateField(key)}>
                        {dateFieldLabels[key]}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <span className="text-primary fw-semibold">for</span>
                <DatePeriodNavigation
                  onPeriodChange={handlePeriodChange}
                  initialDateRange={{ start_date: initialDateParams.start_date, end_date: initialDateParams.end_date }}
                  initialNavigationMode={initialDateParams.period}
                />
                <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                <Button
                  variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setShowFilterBar(!showFilterBar)}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExportCsv}
                  disabled={exporting || isLoading}
                >
                  {exporting ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <i className="bi bi-download me-1"></i>
                  )}
                  Export CSV
                </Button>
              </div>
              <div className="bg-header-control rounded px-3 py-1">
                <span className="text-primary">
                  {allInvoices.length > 0 ? (
                    <>{totalCount ?? allInvoices.length} total invoices</>
                  ) : (
                    'No invoices found'
                  )}
                </span>
              </div>
            </div>
          </div>
          {exportError && (
            <Alert variant="danger" className="mt-2 mb-0 py-1" dismissible onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}
          {showFilterBar && (
            <InvoiceFilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
            />
          )}
        </Card.Header>
        <Card.Body
          className="flex-grow-1 overflow-hidden"
          style={{ padding: 0, minHeight: 0 }}
        >
            <DataTable
              data={allInvoices}
              columns={columns}
              getRowKey={getRowKey}
              sortConfig={{ field: sortField, direction: sortDirection }}
              onSortChange={handleSortChange}
              onRowClick={(invoice) => { setDetailInvoice(invoice); setShowDetailModal(true); }}
              pagination={{
                hasMore,
                limit: FETCH_LIMIT,
              }}
              onLoadMore={handleLoadMore}
              isLoading={isLoading}
              preferencesKey="invoices"
              userId={user?.id}
              userPreferences={userData?.preferences}
              maxHeight="100%"
              enableColumnReorder={true}
              enableColumnResize={true}
              enableColumnVisibility={true}
              enableStickyHeader={true}
            />
        </Card.Body>
      </Card>

      <InvoiceDetailModal
        show={showDetailModal}
        onHide={() => { setShowDetailModal(false); setDetailInvoice(null); }}
        invoice={detailInvoice}
        onInvoiceUpdated={(updated) => {
          setDetailInvoice(updated);
          setAllInvoices(prev => prev.map(inv => inv.id === updated.id ? updated : inv));
        }}
      />
    </DashboardLayout>
  );
};

export default InvoicesPage;
